import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import usePantallaEdit from "hooks/usePantallaEdit";
import { useRequestTracker } from "hooks/useResquestTracker";
import { TerminalPOSManager } from "managers";
import { GridView, SvgComponent } from "components";
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import ListHeader from "components/PantallaList/Header/ListHeader";
import { Simplebar } from "components/Simplebar/Simplebar";
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import Select from "react-select";
import { DEFAULT_PAGINATION_VALUES } from "utils";
import SuspenseECF from "components/Suspense/SuspenseECF";
import Exportar from "components/Exportar/Exportar";
import { TerminalPOS } from "models/ECF/TerminalPOS";
import TerminalPOSEdit from "./TerminalPOSEdit";


const TerminalPOSList = ({ showLoading, hideLoading, hideTitle, isActive }) => {
    const [where, setWhere] = useState({});
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
    const [list, setList] = useState([]);
    const { setPaginationValuesWhere } = usePaginationValuesWhere();
    const { handleException } = useExceptionHandler();
    const [showCanvas, setShowCanvas] = useState(false);
    const [terminalPOS, setTerminalPOSState] = useState();
    const { Message, cerrarAlertas } = useMessage();
    const ref = useRef(null);
    const [showExportar, setShowExportar] = useState(false);
    const { trackRequest } = useRequestTracker();

    const ESTATUS_LIST = [
        { value: "", label: "Todos" },
        ...TerminalPOS.ESTATUS_LIST,
    ];

    const {
        setEntidad: setTerminalPOS,
        setEstadoCargado,
        setHayCambios,
    } = usePantallaEdit(setTerminalPOSState);

    useEffect(() => {
        if (isActive) {
            buscar();
        }
        return () => {
            cerrarAlertas();
            hideLoading();
        };
    }, [where, isActive]);

    useEffect(() => {
        if (terminalPOS != undefined) {
            setShowCanvas(true);
        }
    }, [terminalPOS]);

    const buscar = () => {
        const whereWithPaginationValues = { ...where, ...pagination };
        trackRequest({
            executeRequest: (signal) =>
                TerminalPOSManager.listado(whereWithPaginationValues, {
                    signal,
                }),
            onSuccess: (res) => {
                setPaginationValuesWhere(res.Headers, pagination, setPagination);
                setList(res.Data);
            },
            onError: (error) => handleException(error),
            onFinally: () => hideLoading(),
        });
    };

    const onPageIndexChanging = (pageNumber) => {
        setPagination((prevstate) => {
            return { ...prevstate, IndiceDePagina: pageNumber - 1 };
        });
        setWhere({ ...where });
    };

    const handleClick = (id) => {
        setEstadoCargado(false);
        if (id) {
            TerminalPOSManager.buscarPorId(id)
                .then((data) => {
                    setTerminalPOS(data);
                })
                .catch((e) => {
                    handleException(e);
                })
                .finally(() => {
                    hideLoading();
                    setEstadoCargado(true);
                });
        } else {
            setTerminalPOS({});
            setEstadoCargado(true);
        }
    };

    const grabar = () => {
        ref.current?.showLoading(true);

        if (!terminalPOS.Img) {
            terminalPOS.Img = [];
        }

        if (!terminalPOS.Id) {
            TerminalPOSManager.crear(terminalPOS)
                .then((res) => {
                    Message({ message: `Grabado Exitosamente` });
                    buscar();
                    setTerminalPOS(res);
                    setShowCanvas(false);
                })
                .catch((e) => {
                    handleException(e);
                })
                .finally(() => {
                    ref.current?.showLoading(false);
                    setHayCambios(false);
                });
        } else {
            TerminalPOSManager.actualizar(terminalPOS)
                .then(() => {
                    buscar();
                    Message({ message: `Actualizado Exitosamente` });
                    setShowCanvas(false);
                })
                .catch((e) => {
                    handleException(e);
                })
                .finally(() => {
                    ref.current?.showLoading(false);
                    setHayCambios(false);
                });
        }
    };

    const terminalPOSEdit = (
        <Simplebar
            ref={ref}
            backdrop={"static"}
            confirmBack
            placement={"end"}
            portal={false}
            visible={showCanvas}
            size={"xxl"}
            setShow={(e) => {
                setShowCanvas(e);
            }}
            hideCallback={() => {
                setEstadoCargado(false);
            }}
            useLoading={true}
            title={
                terminalPOS?.IdSec || false ? "Actualizar Terminal POS" : "Crear Terminal POS"
            }
            btnLeft={{
                show: true,
                message: "Grabar",
                isComponent: true,
                size: "45px",
                height: "24px",
                with: "24px",
                iconName: "save",
                isCenter: true,
                func: grabar,
            }}
            btnRight={{
                show: false,
                label: "",
                func: () => { },
            }}>
            <TerminalPOSEdit terminalPOS={terminalPOS} setTerminalPOS={setTerminalPOS} />
        </Simplebar>
    );

    const openExportar = () => {
        setShowExportar(true);
    };

    return (
        <div>
            {showExportar && (
                <Suspense fallback={<SuspenseECF />}>
                    <Exportar
                        PROPIEDADES_EXPORTAR={TerminalPOS.PROPIEDADES_EXPORTAR}
                        showExportar={showExportar}
                        setShowExportar={setShowExportar}
                        showLoading={showLoading}
                        hideLoading={hideLoading}
                        where={where}
                        serialize={true}
                    ></Exportar>
                </Suspense>
            )}

            <div style={{ paddingRight: "6px" }}>
                <ListHeader
                    titulo={hideTitle ? "" : "Terminal POS"}
                    crearNuevo={() => handleClick()}
                    top="0px"
                    exportar={openExportar}
                ></ListHeader>
            </div>
            {showCanvas && (
                <Suspense fallback={<SuspenseECF />}>{terminalPOSEdit}</Suspense>
            )}
            <>
                <GridView
                    pagination={pagination}
                    setPagination={setPagination}
                    allowPaging={pagination.EsPaginable}
                    pageSize={pagination.CantidadPorPagina}
                    pageIndex={pagination.IndiceDePagina}
                    lastPageNumber={pagination.PaginasTotal}
                    dataSource={list}
                    onPageIndexChanging={onPageIndexChanging}
                    where={where}
                    setWhere={setWhere}
                    height="70vh">

                    <GridView.Column
                        isSticky
                        bodyRenderer={(row) => {
                            return (
                                <>
                                    <div className="float-right">
                                        <IconButton onClick={() => handleClick(row.Id)}>
                                            <ToolTip message="Editar">
                                                <div>
                                                    <SvgComponent
                                                        iconName="pencil"
                                                        height="25"
                                                        width="25"
                                                    />
                                                </div>
                                            </ToolTip>
                                        </IconButton>
                                    </div>
                                </>
                            );
                        }} />

                    <GridView.Column
                        headerText="Id"
                        dataField="Id"
                        sortExpression="Id"
                        width="100px" />

                    <GridView.Column
                        headerText="Nombre"
                        dataField="NombreDispositivo"
                        sortExpression="NombreDispositivo"
                        width="250px" />

                    <GridView.Column
                        headerText="Modelo"
                        dataField="ModeloDispositivo"
                        sortExpression="ModeloDispositivo"
                        width="250px" />

                    <GridView.Column
                        headerText="Version de Android"
                        dataField="VersionFirmware"
                        sortExpression="VersionFirmware"
                        width="250px" />

                    <GridView.Column
                        headerText="IMEI"
                        dataField="IMEI"
                        sortExpression="IMEI"
                        width="150px" />

                    <GridView.Column
                        headerText="Configuración"
                        dataField="TipoFacturacion"
                        sortExpression="TipoFacturacion"
                        width="250px" />

                    <GridView.Column
                        headerText="Estatus"
                        dataField="EstatusDesc"
                        sortExpression="EstatusDesc"
                        width="175px"
                        filterRenderer={() => {
                            return (
                                <Select
                                    className="react-select primary"
                                    options={ESTATUS_LIST}
                                    value={ESTATUS_LIST.filter(function (option) {
                                        return (option.value === (where.Estatus ? where.Estatus : ESTATUS_LIST[0].value));
                                    })}
                                    name="Estatus"
                                    onChange={(e) =>
                                        setWhere({
                                            ...where,
                                            Estatus: e.value,
                                        })
                                    }
                                ></Select>
                            );
                        }} />
                </GridView>
            </>
        </div>
    );
};

export default TerminalPOSList;
